import { useEffect, useState } from 'react';

/**
 * Returns `value`, but only after it has stopped changing for `delayMs`.
 *
 * The first shared version of a pattern that previously existed copy-pasted per feature (see
 * `AttendanceAbsentPresentPage.tsx`'s own inline debounce, left as-is rather than migrated here —
 * this hook is for new call sites, not a mandate to churn a file that already works). Anything that
 * fires a network request per keystroke (global search, a filter box) should debounce the VALUE,
 * not the effect that reads it — that keeps the debounce logic in one place instead of a
 * `setTimeout`/`clearTimeout` pair re-written at every call site.
 */
export function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return debounced;
}
